import {
  FileText,
  User,
  MessageCircle,
  AlertTriangle,
  DollarSign,
  Globe,
  Package
} from 'lucide-react';

/**
 * Centralized offboarding step configuration
 * Single source of truth for all step definitions
 */

/**
 * Get offboarding steps configuration with conditional IR21 step
 * @param {Object} workflowData - The workflow data object
 * @param {string} workflowId - The workflow ID for route generation
 * @returns {Array} Array of step configuration objects
 */
export const getOffboardingSteps = (workflowData, workflowId) => {
  const requiresIR21 = workflowData?.requires_ir21;

  const baseSteps = [
    {
      id: 1,
      name: 'Resignation Details',
      description: 'Record resignation details',
      icon: FileText,
      completed: workflowData?.step_1_completed || false,
      current: workflowData?.current_step === 1,
      route: `/admin/employee-lifecycle/offboarding/${workflowId}/step-1`
    },
    {
      id: 2,
      name: 'Employment End Date',
      description: 'Confirm last day of service',
      icon: User,
      completed: workflowData?.step_2_completed || false,
      current: workflowData?.current_step === 2,
      route: `/admin/employee-lifecycle/offboarding/${workflowId}/step-2`
    },
    {
      id: 3,
      name: 'HOD Discussion',
      description: 'Leave settlement and exit interview',
      icon: MessageCircle,
      completed: workflowData?.step_3_completed || false,
      current: workflowData?.current_step === 3,
      route: `/admin/employee-lifecycle/offboarding/${workflowId}/step-3`
    },
    {
      id: 4,
      name: 'Bond Check',
      description: 'Automatic bond assessment',
      icon: AlertTriangle,
      completed: workflowData?.step_4_completed || false,
      current: workflowData?.current_step === 4,
      route: `/admin/employee-lifecycle/offboarding/${workflowId}/step-4`
    },
    {
      id: 5,
      name: 'Final Settlement',
      description: 'Calculate final payment',
      icon: DollarSign,
      completed: workflowData?.step_5_completed || false,
      current: workflowData?.current_step === 5,
      route: `/admin/employee-lifecycle/offboarding/${workflowId}/step-5`
    }
  ];

  // Conditionally add IR21 step only for non-Singapore citizens
  if (requiresIR21) {
    baseSteps.push({
      id: 6,
      name: 'IR21 Processing',
      description: 'Foreign worker compliance',
      icon: Globe,
      completed: workflowData?.step_6_completed || false,
      current: workflowData?.current_step === 6,
      route: `/admin/employee-lifecycle/offboarding/${workflowId}/step-6`
    });
  }

  // Add final exit step (adjust ID based on whether IR21 was included)
  baseSteps.push({
    id: requiresIR21 ? 7 : 6,
    name: 'Final Offboarding',
    description: 'Complete offboarding',
    icon: Package,
    completed: requiresIR21 ? workflowData?.step_7_completed : workflowData?.step_6_completed,
    current: workflowData?.current_step === (requiresIR21 ? 7 : 6),
    route: `/admin/employee-lifecycle/offboarding/${workflowId}/step-${requiresIR21 ? 7 : 6}`
  });

  return baseSteps;
};

/**
 * Calculate offboarding progress percentage
 * Correctly handles workflows with and without IR21 requirement
 * @param {Object} workflow - The workflow object
 * @returns {number} Progress percentage (0-100)
 */
export const calculateProgressPercentage = (workflow) => {
  if (!workflow) return 0;

  const requiresIR21 = workflow.requires_ir21;

  // Base steps: Resignation, HOD Discussion, Exit Interview, Bond Check, Final Settlement
  const baseCompleted = [
    workflow.step_1_completed,
    workflow.step_2_completed,
    workflow.step_3_completed,
    workflow.step_4_completed,
    workflow.step_5_completed
  ].filter(Boolean).length;

  // IR21 step - only count if required
  const ir21Completed = requiresIR21 ? (workflow.step_6_completed ? 1 : 0) : 0;

  // Final offboarding step (step 6 or 7 depending on IR21 requirement)
  const finalStepCompleted = requiresIR21 
    ? (workflow.step_7_completed ? 1 : 0)
    : (workflow.step_6_completed ? 1 : 0);

  // Calculate total
  const totalCompleted = baseCompleted + ir21Completed + finalStepCompleted;
  const totalSteps = requiresIR21 ? 7 : 6;

  return Math.round((totalCompleted / totalSteps) * 100);
};

/**
 * Get the final step number for a workflow
 * @param {Object} workflow - The workflow object
 * @returns {number} Final step number (6 or 7)
 */
export const getFinalStepNumber = (workflow) => {
  return workflow?.requires_ir21 ? 7 : 6;
};

/**
 * Get step name by number
 * @param {number} stepNumber - The step number
 * @returns {string} Step name
 */
export const getStepName = (stepNumber) => {
  const stepNames = {
    1: 'Resignation Details',
    2: 'Employment End Date',
    3: 'HOD Discussion',
    4: 'Bond Check',
    5: 'Final Settlement',
    6: 'IR21 Processing',
    7: 'Final Offboarding'
  };
  return stepNames[stepNumber] || 'Unknown Step';
};

